import { useState, type MouseEvent } from 'react';
import { ExternalLink, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  OnboardingTable,
  type OnboardingTableColumn,
} from '@/components/ui/onboarding-table';
import {
  type DeletionMode,
  useDeleteComponent,
} from '../api/deleteComponent';
import DeleteComponentDialog from './DeleteComponentDialog';
import type { OnboardedData } from '../api/getComponentList';
import TechnologySymbol from './TechnologySymbol';
import { cn } from '@/lib/utils';
import { getComponentKind } from './componentInventoryUtils';
import { filterCardTechnologies } from '@/utils/technologySymbols';
import ComponentDeletedActions from './ComponentDeletedActions';

const statusPillBase =
  'inline-flex items-center whitespace-nowrap rounded-full px-[0.55rem] py-[0.2rem] text-[0.6875rem] font-semibold uppercase leading-[1.2] tracking-[0.02em]';
const statusPillActive =
  'border border-[#abefc6] bg-[#ecfdf3] text-[#027a48]';
const statusPillScanning =
  'border border-[#fedf89] bg-[#fffaeb] text-[#b54708]';

const MAX_VISIBLE_TECH = 3;

interface ComponentTableViewProps {
  data: OnboardedData[];
  onSelectComponent: (repo: OnboardedData) => void;
}

const ComponentTableView = ({
  data,
  onSelectComponent,
}: ComponentTableViewProps) => {
  const [repoToDelete, setRepoToDelete] = useState<OnboardedData | null>(null);
  const { mutate: deleteComponent, isPending: isDeleting } =
    useDeleteComponent();

  const handleDeleteClick = (e: MouseEvent, repo: OnboardedData) => {
    e.stopPropagation();
    setRepoToDelete(repo);
  };

  const handleConfirmDelete = (deletionMode: DeletionMode) => {
    if (!repoToDelete) return;
    deleteComponent(
      { branchId: repoToDelete.branchId, deletionMode },
      {
        onSuccess: () => setRepoToDelete(null),
        onError: err => {
          console.error('[deleteComponent]', err);
          window.alert(
            err?.message ||
              'Delete failed. Check the API/backend logs and try again.',
          );
        },
      },
    );
  };

  const isRowPendingDeletion = (row: OnboardedData) =>
    Boolean(
      row.pendingDeletion?.branchId === row.branchId &&
        row.pendingDeletion?.status !== 'failed',
    );

  const isRowSoftDeleted = (row: OnboardedData) => Boolean(row.isDeleted);

  const willAwaitDeinstrumentPr = (row: OnboardedData | null) => {
    if (!row) return false;
    if (String(row.prStatus || '').toUpperCase() === 'NOT_REQUIRED') return false;
    const hasPr = (p?: OnboardedData['instrumentationPr']) =>
      Boolean(p?.prId) ||
      ['raised', 'open', 'merged', 'approved'].includes(
        String(p?.status || '').toLowerCase(),
      );
    return hasPr(row.instrumentationPr) || hasPr(row.testInstrumentationPr);
  };

  const columns: OnboardingTableColumn<OnboardedData>[] = [
    {
      key: 'repoName',
      header: 'Name',
      render: row => (
        <Button
          type="button"
          variant="ghost"
          className={cn(
            'h-auto p-0 text-left font-normal text-[var(--inv-text)]',
            !isRowSoftDeleted(row) && 'hover:bg-transparent hover:underline',
          )}
          disabled={isRowSoftDeleted(row)}
          onClick={() => {
            if (!isRowSoftDeleted(row)) onSelectComponent(row);
          }}>
          {row.isView ? row.displayName : row.repoName}
        </Button>
      ),
    },
    {
      key: 'projectName',
      header: 'Project',
      render: row => row.projectName?.trim() || '-',
    },
    {
      key: 'branchName',
      header: 'Branch',
      render: row => row.branchName,
    },
    {
      key: 'environment',
      header: 'Environment',
      render: row => row.environment,
    },
    {
      key: 'repoStatus',
      header: 'Status',

      render: row =>
        isRowSoftDeleted(row) ? (
          <span className={cn(statusPillBase, 'border-[var(--inv-border)] bg-[var(--inv-surface-muted)] text-[var(--inv-text-muted)]')}>
            Deleted
          </span>
        ) : isRowPendingDeletion(row) ? (
          <span className={cn(statusPillBase, statusPillScanning)}>
            Pending deletion
          </span>
        ) : (
          <span
            className={cn(
              statusPillBase,
              row.repoStatus === 'ACTIVE'
                ? statusPillActive
                : statusPillScanning,
            )}>
            {row.repoStatus === 'ACTIVE' ? 'Active' : 'Scanning'}
          </span>
        ),
    },
    {
      key: 'lineCoverage',
      header: 'Line Coverage',
      render: row => <span>{row.metrics?.linesOfCodeCoverage ?? 0}%</span>,
    },
    {
      key: 'invCoverage',
      header: 'Inv. Coverage',
      render: row => <span>{row.metrics?.inventoryModeCoverage ?? 0}%</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      render: row => (
        <div className="flex items-center gap-1">
          {isRowSoftDeleted(row) ? (
            <ComponentDeletedActions
              branchId={row.branchId}
              name={row.isView ? row.displayName : row.repoName}
            />
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                aria-label="Open component"
                onClick={() => onSelectComponent(row)}>
                <ExternalLink className="h-4 w-4" />
              </Button>
              {!row.isView && !isRowPendingDeletion(row) && (
                <Button
                  type="button"
                  variant="icon"
                  size="sm"
                  aria-label="Delete component"
                  onClick={(e: MouseEvent) => handleDeleteClick(e, row)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </>
          )}
        </div>
      ),
    },
    {
      key: 'detectedTechnologies',
      header: 'Technology',
      render: row => {
        const technologies = filterCardTechnologies(
          row.detectedTechnologies?.filter(Boolean) ?? [],
          getComponentKind(row),
        );
        if (technologies.length === 0) {
          return '—';
        }
        const visibleTech = technologies.slice(0, MAX_VISIBLE_TECH);
        const hiddenTechCount = Math.max(
          technologies.length - MAX_VISIBLE_TECH,
          0,
        );
        return (
          <span
            className="inline-flex flex-nowrap items-center gap-1"
            title={technologies.join(', ')}>
            {visibleTech.map(tech => (
              <TechnologySymbol key={tech} technology={tech} size="sm" />
            ))}
            {hiddenTechCount > 0 && (
              <span className="text-[0.6875rem] font-semibold text-[var(--inv-text-muted)]">
                +{hiddenTechCount}
              </span>
            )}
          </span>
        );
      },
    },
  ];

  return (
    <>
      <OnboardingTable
        data={data}
        columns={columns}
        rowKey={row => row.dbRepoId}
        rowClassName={row =>
          isRowSoftDeleted(row) ? 'opacity-55 saturate-[0.65]' : undefined
        }
        className="mb-0"
      />

      <DeleteComponentDialog
        open={!!repoToDelete}
        onOpenChange={open => {
          if (!open) setRepoToDelete(null);
        }}
        componentName={
          repoToDelete?.isView
            ? (repoToDelete.displayName ?? repoToDelete.repoName)
            : (repoToDelete?.repoName ?? '')
        }
        willAwaitDeinstrumentPr={willAwaitDeinstrumentPr(repoToDelete)}
        isDeleting={isDeleting}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default ComponentTableView;
